import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Row,
  Modal,
  Form,
  FormGroup,
  Col,
  Card,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import ClipLoader from "react-spinners/ClipLoader";
import {
  DocType,
  QboAccountCategory,
  QboItem,
  TagInvoiceRule,
} from "src/docuclipper/DocuclipperTypes";
import TagRegexesInvoiceViewOnly from "./TagRegexesInvoiceViewOnly";
import {
  addNewInvoiceGroup,
  deleteTagRegexesInvoiceAction,
  fetchTagRegexesInvoice,
  updateTagRegexesInvoiceAction,
} from "src/redux/reducers/TagRegexesInvoice";
import { ReduxState } from "src/redux";
import {
  applyRunInvoiceRules,
  setShowModalInvoiceRules,
} from "src/redux/reducers/InvoiceManager";
import { LoadingButton } from "src/views/Docuclipper/TrackedButton";
import ErrorMessage from "src/views/Docuclipper/ErrorMessage";
import {
  searchQboAccounts,
  searchQboItems,
  searchXeroAccounts,
  searchXeroItems,
  searchXeroContacts,
  searchQboCustomers,
  searchQboVendors,
} from "src/docuclipper/api";
import { SUPPORTED_SCOPES } from "src/redux/reducers/TagLevelIntegration";

type Props = {
  categories?: QboItem[] | QboAccountCategory[];
  docType: DocType;
  integrationId?: number | null;
};

type Rule = {
  ifConditions: {
    field: string;
    operator: string;
    value: { keywords: string[] };
  }[];
  thenSet: { categoryAutomated: string; value: string; valueLabel?: string }[];
  createdAt: string;
};

const defaultErrorMap = {
  keyword: false,
  category: false,
};

export const TagRegexesInvoice: React.FC<Props> = ({
  categories = [],
  docType,
  integrationId = null,
}) => {
  const dispatch = useDispatch();
  const [combinationOperator, setCombinationOperator] = useState("all");
  const [rules, setRules] = useState<Rule[]>([]);
  const [editRuleIndex, setEditRuleIndex] = useState<number | null>(null);
  const [addingNewRule, setAddingNewRule] = useState(false);
  const [errorMap, setErrorMap] = useState<{ [key: string]: boolean }>({
    ...defaultErrorMap,
  });

  const { loading, tagRegexesInvoice } = useSelector(
    (state: ReduxState) => state.TagRegexesInvoice
  );

  const { showModalInvoiceRules, runInvoiceRulesLoading } = useSelector(
    (state: ReduxState) => state.InvoiceManager
  );

  const linkedIntegrationDetails = useSelector(
    (state: ReduxState) => state.TagLevelIntegration.linkedIntegrationDetails
  );

  // Get Xero accounts and items from Redux for Xero integrations
  const xeroAccounts = useSelector(
    (state: ReduxState) => state.XeroAccount.xeroAccounts
  );
  const xeroItems = useSelector(
    (state: ReduxState) => state.XeroItem.xeroItems
  );

  // Detect if this is a Xero integration
  const isXero = linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.xero;

  // Normalize docType: when integration is disconnected, convert QuickBooks/Xero-specific types
  // to generic payables/receivables for free-form text fields
  const normalizedDocType: DocType = (() => {
    // If there's no integration, convert specific docTypes to generic ones
    if (!integrationId) {
      if (docType === "bill" || docType === "expense" || docType === "creditNote") {
        return "payables";
      }
      if (docType === "invoice" || docType === "debitNote") {
        return "receivables";
      }
    }
    // Otherwise, use the docType as-is
    return docType;
  })();

  const PAYABLES_TYPES = ["payables", "expense", "bill", "creditNote"];
  const RECEIVABLES_TYPES = ["receivables", "invoice", "debitNote"];

  // For Xero, show both Account and Item/Service options
  // For non-Xero, use Category/Product/Service terminology
  const categoryLabel = PAYABLES_TYPES.includes(normalizedDocType)
    ? "Category"
    : "Product/Service";

  // For Xero, use "Account" (not "Category") and "Item/Service"
  // For payables/receivables (non-Xero), show both Category and Product/Service as free-form text fields
  // Customer/Vendor are only available when an integration is connected — they
  // resolve to a provider contact ID, which only makes sense for QBO/Xero.
  const partyKey = integrationId
    ? PAYABLES_TYPES.includes(normalizedDocType)
      ? "Vendor"
      : RECEIVABLES_TYPES.includes(normalizedDocType)
      ? "Customer"
      : null
    : null;

  const baseKeys = isXero
    ? ["Account", "Item/Service"]
    : (PAYABLES_TYPES.includes(normalizedDocType) || RECEIVABLES_TYPES.includes(normalizedDocType))
    ? ["Category", "Product/Service"]
    : [categoryLabel];

  const keys = partyKey ? [...baseKeys, partyKey] : baseKeys;

  const availableFields = [
    { value: "description", label: "Line Item Description" },
    // Add vendor or customer field based on normalizedDocType
    ...(PAYABLES_TYPES.includes(normalizedDocType)
      ? [{ value: "vendor", label: "Vendor" }]
      : [{ value: "customer", label: "Customer" }]),
  ];
  const toggle = () => {
    dispatch(setShowModalInvoiceRules(!showModalInvoiceRules));

    setEditRuleIndex(null);
    setErrorMap({ ...defaultErrorMap });
  };

  const groupName = `DocType: ${normalizedDocType} | ${
    integrationId ? `Integration ${integrationId}` : "Invoice"
  }`;

  // Helper function to format account label with code and name
  const formatAccountLabel = (account: { name: string; code?: string }) => {
    if (account.code) {
      return `${account.code} - ${account.name}`;
    }
    return account.name;
  };

  // Helper function to format item label with code and name
  const formatItemLabel = (item: { name: string; code?: string }) => {
    if (item.code) {
      return `${item.code} - ${item.name}`;
    }
    return item.name;
  };

  // Helper function to format account type for display
  const formatAccountType = (type: string) => {
    return type
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Helper function to group accounts by type
  const groupAccountsByType = (accounts: typeof xeroAccounts) => {
    const grouped: { [key: string]: typeof xeroAccounts } = {};

    accounts.forEach((account) => {
      const type = account.type || "OTHER";
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(account);
    });

    return Object.keys(grouped)
      .sort()
      .map((type) => ({
        label: formatAccountType(type),
        options: grouped[type].map((account) => ({
          label: formatAccountLabel(account),
          value: account.value,
        })),
      }));
  };

  const categoryOptions = ["payables", "receivables"].includes(normalizedDocType)
    ? []
    : (categories || []).map((option) => ({
        value: option.value,
        label: option.name,
      }));

  // Debounced load options for async search
  const loadOptionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadCategoryOrServiceOptions = useCallback(
    (inputValue: string): Promise<Array<{ value: string; label: string }>> => {
      return new Promise((resolve) => {
        // Clear any existing timeout
        if (loadOptionsTimeoutRef.current) {
          clearTimeout(loadOptionsTimeoutRef.current);
        }

        if (!integrationId) {
          resolve([]);
          return;
        }

        // If input is null/undefined or too short, return default options or empty array
        if (!inputValue || inputValue.length < 2) {
          // For Xero, return both accounts (grouped) and items when no search term
          if (isXero && xeroAccounts.length > 0 && xeroItems.length > 0) {
            const accountGroups = groupAccountsByType(xeroAccounts);
            const itemOptions = xeroItems.slice(0, 25).map((item) => ({
              label: formatItemLabel(item),
              value: item.value,
            }));
            // Combine: first show some items, then account groups
            resolve(
              [
                ...itemOptions,
                ...accountGroups.flatMap((group) => group.options.slice(0, 10)),
              ].slice(0, 50)
            );
            return;
          }
          resolve(categoryOptions.slice(0, 50));
          return;
        }

        // Set new timeout
        loadOptionsTimeoutRef.current = setTimeout(async () => {
          try {
            // For Xero, search both accounts and items
            if (isXero && integrationId) {
              const searchTerm = inputValue.toLowerCase();

              // Search accounts
              const accountResults = await searchXeroAccounts(
                Number(integrationId),
                inputValue
              );
              const accountOptions = accountResults.map((a: any) => ({
                label: formatAccountLabel({
                  name: a.name || a.Name,
                  code: a.code || a.Code,
                }),
                value: a.accountID || a.AccountID,
              }));

              // Search items
              const itemResults = await searchXeroItems(
                Number(integrationId),
                inputValue
              );
              const itemOptions = itemResults.map((i: any) => ({
                label: formatItemLabel({
                  name: i.name || i.Name,
                  code: i.code || i.Code,
                }),
                value: i.itemID || i.ItemID,
              }));

              // Combine and return both
              resolve([...accountOptions, ...itemOptions].slice(0, 50));
              return;
            }

            // Determine if we're searching for categories or services/items
            const isCategory = ["payables", "expense", "bill"].includes(
              normalizedDocType
            );

            // If we have categories data (e.g., from QuickBooks), filter from it instead of making API calls
            if (categories && categories.length > 0) {
              const searchTerm = inputValue.toLowerCase();
              const filtered = categories
                .filter((cat: any) => {
                  const name = (cat.name || "").toLowerCase();
                  return name.includes(searchTerm);
                })
                .map((cat: any) => ({
                  label: cat.name,
                  value: cat.value,
                }))
                .slice(0, 50);
              resolve(filtered);
              return;
            }

            // Fallback to QuickBooks API calls if no categories data available
            if (isCategory) {
              // Search accounts/categories
              const results = await searchQboAccounts(
                Number(integrationId),
                false,
                inputValue
              );
              resolve(
                results.map((c: any) => ({
                  label: c.Name,
                  value: c.Id,
                }))
              );
            } else {
              // Search items/services
              const results = await searchQboItems(
                Number(integrationId),
                inputValue
              );
              resolve(
                results.map((i: any) => ({
                  label: i.Name,
                  value: i.Id,
                }))
              );
            }
          } catch (error) {
            console.error("Error loading options:", error);
            resolve([]);
          }
        }, 400); // 400ms debounce delay
      });
    },
    [integrationId, normalizedDocType, categoryOptions, categories, isXero]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (loadOptionsTimeoutRef.current) {
        clearTimeout(loadOptionsTimeoutRef.current);
      }
    };
  }, []);

  const contactLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    return () => {
      if (contactLoadTimeoutRef.current) {
        clearTimeout(contactLoadTimeoutRef.current);
      }
    };
  }, []);

  const loadContactOptions = useCallback(
    (kind: "Customer" | "Vendor") =>
      (inputValue: string): Promise<Array<{ value: string; label: string }>> => {
        return new Promise((resolve) => {
          if (contactLoadTimeoutRef.current) {
            clearTimeout(contactLoadTimeoutRef.current);
          }
          if (!integrationId) {
            resolve([]);
            return;
          }
          contactLoadTimeoutRef.current = setTimeout(async () => {
            try {
              if (isXero) {
                const res = await searchXeroContacts(
                  Number(integrationId),
                  inputValue || ""
                );
                const contacts: any[] = res?.contacts || res || [];
                resolve(
                  contacts
                    .map((c) => ({
                      label: c.name || c.Name || "",
                      value: c.contactID || c.ContactID || "",
                    }))
                    .filter((o) => o.value)
                    .slice(0, 50)
                );
                return;
              }
              if (kind === "Customer") {
                const res = await searchQboCustomers(
                  Number(integrationId),
                  inputValue || ""
                );
                const list: any[] =
                  res?.QueryResponse?.Customer || res?.customers || res || [];
                resolve(
                  list
                    .map((c) => ({
                      label: c.DisplayName || c.Name || "",
                      value: c.Id || "",
                    }))
                    .filter((o) => o.value)
                    .slice(0, 50)
                );
              } else {
                const res = await searchQboVendors(
                  Number(integrationId),
                  inputValue || ""
                );
                const list: any[] =
                  res?.QueryResponse?.Vendor || res?.vendors || res || [];
                resolve(
                  list
                    .map((v) => ({
                      label: v.DisplayName || v.Name || "",
                      value: v.Id || "",
                    }))
                    .filter((o) => o.value)
                    .slice(0, 50)
                );
              }
            } catch (err) {
              resolve([]);
            }
          }, 250);
        });
      },
    [integrationId, isXero]
  );

  // Always use "categoryAutomated" and "serviceAutomated" keys for both Xero and QuickBooks
  const categoryKey = "categoryAutomated";
  const serviceKey = "serviceAutomated";

  const ruleToEdit = editRuleIndex !== null ? rules[editRuleIndex] : null;

  const selectedFields = ruleToEdit
    ? ruleToEdit.ifConditions.map((condition) => condition.field)
    : [];
  const remainingFields = availableFields.filter(
    (field) => !selectedFields.includes(field.value)
  );
  const selectedCategories = ruleToEdit
    ? ruleToEdit.thenSet
        .map((action) => action.categoryAutomated)
        .filter((cat) => cat && cat !== "") // Filter out empty strings
    : [];
  const remainingCategories = keys.filter(
    (key) => !selectedCategories.includes(key)
  );

  const hasError = Object.values(errorMap).some((value) => value);

  const handleRuleChange = (ruleIndex, index, field, value, valueLabel?: string) => {
    const newRules = [...rules];
    if (field === "categoryAutomated") {
      newRules[ruleIndex].thenSet[index][field] = value;
      // Reset value when the action type changes to avoid mixing IDs across types
      newRules[ruleIndex].thenSet[index].value = "";
      newRules[ruleIndex].thenSet[index].valueLabel = "";
    } else {
      newRules[ruleIndex].thenSet[index].value = value;
      if (typeof valueLabel === "string") {
        newRules[ruleIndex].thenSet[index].valueLabel = valueLabel;
      }
    }
    setRules(newRules);
  };

  const handleIfConditionChange = (ruleIndex, index, field, value) => {
    const newRules = [...rules];
    if (field === "operator") {
      newRules[ruleIndex].ifConditions[index][field] = value;
      newRules[ruleIndex].ifConditions[index].value = { keywords: [""] };
    } else if (field === "value") {
      if (typeof value === "string") {
        // For all fields, including vendor and customer, use the keywords array
        newRules[ruleIndex].ifConditions[index].value = {
          keywords: value.split(",").map((keyword) => keyword.trim()),
        };
      } else {
        // If it's already in the correct format, just assign it
        newRules[ruleIndex].ifConditions[index].value = value;
      }
    } else {
      newRules[ruleIndex].ifConditions[index][field] = value;
    }
    setRules(newRules);
  };

  const handleOnCreateRule = () => {
    const lastRule = rules[rules.length - 1];
    const firstCondition = lastRule.ifConditions[0];

    if (firstCondition.value.keywords[0] === "") {
      setErrorMap({ ...errorMap, keyword: true });
      return;
    }

    if (lastRule.thenSet[0].value === "") {
      setErrorMap({ ...errorMap, category: true });
      return;
    }

    setErrorMap({ ...errorMap, keyword: false, category: false });

    const newRule = lastRule;

    // Process all actions in thenSet, not just the first one
    const params: any = { docType: normalizedDocType };

    // Initialize category and service as empty strings
    params[categoryKey] = "";
    params[serviceKey] = "";

    // Process each action in thenSet
    newRule.thenSet.forEach((action) => {
      if (
        action.categoryAutomated === "Category" ||
        (isXero && action.categoryAutomated === "Account")
      ) {
        params[categoryKey] = action.value || "";
      } else if (
        action.categoryAutomated === "Product/Service" ||
        (isXero && action.categoryAutomated === "Item/Service")
      ) {
        params[serviceKey] = action.value || "";
      } else if (action.categoryAutomated === "Customer") {
        params.customerAutomated = action.value || "";
        params.customerAutomatedName = action.valueLabel || "";
      } else if (action.categoryAutomated === "Vendor") {
        params.vendorAutomated = action.value || "";
        params.vendorAutomatedName = action.valueLabel || "";
      }
    });

    const tag: TagInvoiceRule = {
      tag: `${groupName} - Rule ${rules.length}`,
      jsonRules: JSON.stringify({
        conditions: {
          [combinationOperator]: newRule.ifConditions.map((condition) => ({
            fact: condition.field,
            operator: condition.operator,
            value: condition.value,
          })),
        },
        event: {
          type: "invoiceRules",
          params,
        },
      }),
    };
    dispatch(addNewInvoiceGroup(groupName, tag, integrationId, normalizedDocType));
    setAddingNewRule(false);
    setEditRuleIndex(null);
  };

  const handleOnBlurUpdateRule = (ruleIndex) => {
    if (addingNewRule) return;

    const ruleToUpdate = rules[ruleIndex];
    const tagRegex = tagRegexesInvoice[ruleIndex];

    // If we don't have a tagRegex to update, return early
    if (!tagRegex?.id) return;

    // Process all actions in thenSet, not just the first one
    const params: any = { docType: normalizedDocType };

    // Initialize category and service as empty strings
    params[categoryKey] = "";
    params[serviceKey] = "";

    // Process each action in thenSet
    ruleToUpdate.thenSet.forEach((action) => {
      if (
        action.categoryAutomated === "Category" ||
        (isXero && action.categoryAutomated === "Account")
      ) {
        params[categoryKey] = action.value || "";
      } else if (
        action.categoryAutomated === "Product/Service" ||
        (isXero && action.categoryAutomated === "Item/Service")
      ) {
        params[serviceKey] = action.value || "";
      } else if (action.categoryAutomated === "Customer") {
        params.customerAutomated = action.value || "";
        params.customerAutomatedName = action.valueLabel || "";
      } else if (action.categoryAutomated === "Vendor") {
        params.vendorAutomated = action.value || "";
        params.vendorAutomatedName = action.valueLabel || "";
      }
    });

    const jsonRule = {
      conditions: {
        [combinationOperator]: ruleToUpdate.ifConditions.map((condition) => ({
          fact: condition.field,
          operator: condition.operator,
          value: condition.value,
        })),
      },
      event: {
        type: "invoiceRules",
        params,
      },
    };

    dispatch(
      updateTagRegexesInvoiceAction(
        tagRegex.id,
        jsonRule,
        integrationId,
        normalizedDocType
      )
    );
  };

  const handleClearAddRule = () => {
    setAddingNewRule(false);
    setEditRuleIndex(null);
    setErrorMap({ ...defaultErrorMap });
  };

  const handleOnRemoveRule = (ruleIndex) => {
    if (addingNewRule && ruleIndex === rules.length - 1) {
      setRules(rules.filter((_, index) => index !== ruleIndex));
      handleClearAddRule();
    } else {
      const ruleToRemove = tagRegexesInvoice[ruleIndex];
      if (ruleToRemove) {
        dispatch(
          deleteTagRegexesInvoiceAction(ruleToRemove.id, integrationId, normalizedDocType)
        );
        setRules(rules.filter((_, index) => index !== ruleIndex));
        handleClearAddRule();
        // dispatch(applyRunInvoiceRules(integrationId, docType));
      }
    }
  };

  const handleAddRule = () => {
    // For Xero, default to "Account" for bills/expenses, "Item/Service" for invoices
    // For payables/receivables (non-Xero), default to "Category" for payables, "Product/Service" for receivables
    const defaultCategory = isXero
      ? ["payables", "expense", "bill"].includes(normalizedDocType)
        ? "Account"
        : "Item/Service"
      : ["payables", "receivables"].includes(normalizedDocType)
      ? ["payables"].includes(normalizedDocType)
        ? "Category"
        : "Product/Service"
      : categoryLabel;
    const newRule = {
      ifConditions: [
        {
          field: "description",
          operator: "containsAny",
          value: { keywords: [""] },
        },
      ],
      thenSet: [{ categoryAutomated: defaultCategory, value: "" }],
      createdAt: new Date().toISOString(),
    };
    const newRules = [...rules, newRule];
    setRules(newRules);
    setEditRuleIndex(newRules.length - 1);
    setAddingNewRule(true);
  };

  const handleAddThenSet = (ruleIndex) => {
    const newRules = [...rules];
    // For Xero, default to first available option
    // For payables/receivables (non-Xero), default to first available option from keys
    // Otherwise use categoryLabel
    const defaultCategory = isXero
      ? remainingCategories.length > 0
        ? remainingCategories[0]
        : keys[0]
      : ["payables", "receivables"].includes(normalizedDocType)
      ? remainingCategories.length > 0
        ? remainingCategories[0]
        : keys[0]
      : categoryLabel;
    newRules[ruleIndex].thenSet.push({
      categoryAutomated: defaultCategory,
      value: "",
    });
    setRules(newRules);
  };

  const handleAddIfCondition = (ruleIndex) => {
    const newRules = [...rules];
    newRules[ruleIndex].ifConditions.push({
      field: "description",
      operator: "containsAny",
      value: { keywords: [""] },
    });
    setRules(newRules);
  };

  const handleRemoveThenSet = (ruleIndex, index) => {
    if (rules[ruleIndex].thenSet.length > 1) {
      const newRules = [...rules];
      newRules[ruleIndex].thenSet.splice(index, 1);
      setRules(newRules);
    }
  };

  const handleRemoveIfCondition = (ruleIndex, index) => {
    if (rules[ruleIndex].ifConditions.length > 1) {
      const newRules = [...rules];
      newRules[ruleIndex].ifConditions.splice(index, 1);
      setRules(newRules);
    }
  };

  useEffect(() => {
    if (showModalInvoiceRules) {
      dispatch(fetchTagRegexesInvoice("invoiceRules", integrationId, normalizedDocType));
    }
  }, [showModalInvoiceRules]);

  useEffect(() => {
    const existingRules = tagRegexesInvoice.map((tagRegex) => {
      const baseRule = {
        conditions: {
          all: [
            {
              fact: "description",
              operator: "containsAny",
              value: { keywords: [""] },
            },
          ],
        },
        event: {
          type: "invoiceRules",
          params: {
            [categoryKey]: "",
            [serviceKey]: "",
          },
        },
      };

      const parsedRules = (tagRegex?.jsonRules as any) || baseRule;

      const operator = parsedRules.conditions?.all ? "all" : "any";

      // Build thenSet array from params - can have both category and service
      const thenSet: any[] = [];
      const params = parsedRules.event?.params || {};

      // Add category action if present
      if (params[categoryKey]) {
        thenSet.push({
          categoryAutomated: isXero ? "Account" : "Category",
          value: params[categoryKey],
        });
      }

      // Add service action if present
      if (params[serviceKey]) {
        thenSet.push({
          categoryAutomated: isXero ? "Item/Service" : "Product/Service",
          value: params[serviceKey],
        });
      }

      // If no actions found, add a default one
      if (thenSet.length === 0) {
        thenSet.push({
          categoryAutomated: isXero
            ? ["payables", "expense", "bill"].includes(normalizedDocType)
              ? "Account"
              : "Item/Service"
            : ["payables", "expense", "bill"].includes(normalizedDocType)
            ? "Category"
            : "Product/Service",
          value: "",
        });
      }

      return {
        ifConditions: parsedRules.conditions[operator]?.map((condition) => ({
          field: condition.fact,
          operator: condition.operator,
          value: condition.value.keywords
            ? { keywords: condition.value.keywords }
            : condition.value,
        })),
        thenSet,
        createdAt: tagRegex.createdAt,
      };
    });

    setRules(existingRules);
  }, [tagRegexesInvoice, combinationOperator]);

  return (
    <Modal size="lg" show={showModalInvoiceRules} onHide={toggle}>
      {/* @ts-ignore - react-bootstrap 1.6.1 type definition issue */}
      <Modal.Header closeButton>
        <Modal.Title>Edit Rules</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          height: "35em",
          overflowY: rules.length > 1 ? "scroll" : "hidden",
        }}
      >
        {loading && <ClipLoader />}
        {!loading && (
          <>
            <Row>
              {editRuleIndex === null ? (
                <Col>
                  <Button variant="link" onClick={handleAddRule}>
                    <FontAwesomeIcon icon="plus" />
                    <span className="ml-1">Add new rule</span>
                  </Button>
                </Col>
              ) : (
                <Col>
                  <Button
                    variant="link"
                    disabled={runInvoiceRulesLoading || hasError}
                    onClick={() => {
                      if (addingNewRule) {
                        handleOnRemoveRule(rules.length - 1);
                      } else {
                        setEditRuleIndex(null);
                      }
                    }}
                    className="float-right"
                  >
                    View all rules
                  </Button>
                </Col>
              )}
            </Row>
            {editRuleIndex === null ? (
              <>
                <TagRegexesInvoiceViewOnly
                  rules={rules}
                  categories={categories}
                  onEdit={setEditRuleIndex}
                  onRemove={handleOnRemoveRule}
                />
              </>
            ) : (
              <Row className="flex-column">
                {ruleToEdit && (
                  <Col style={{ maxHeight: "auto", marginBottom: "0" }}>
                    <Card className="mb-4">
                      <Card.Body>
                        <Row>
                          <FormGroup as={Col} className="d-flex">
                            <Form.Label>If</Form.Label>
                            <Select
                              options={[{ value: "all", label: "All" }]}
                              value={{
                                value: combinationOperator,
                                label:
                                  combinationOperator === "all" ? "All" : "Any",
                              }}
                              onChange={(option) =>
                                option && setCombinationOperator(option.value)
                              }
                              className="col-4"
                            />
                          </FormGroup>
                          <Col className="text-right">
                            <Button
                              variant="link"
                              onClick={() => handleOnRemoveRule(editRuleIndex)}
                            >
                              <FontAwesomeIcon icon="trash" />
                            </Button>
                          </Col>
                        </Row>
                        {ruleToEdit.ifConditions.map((condition, index) => (
                          <Form key={index}>
                            <Form.Row>
                              <FormGroup as={Col} md="4">
                                <Select
                                  options={availableFields}
                                  value={{
                                    value: condition.field,
                                    label:
                                      condition.field.charAt(0).toUpperCase() +
                                      condition.field.slice(1),
                                  }}
                                  onChange={(option) => {
                                    if (option) {
                                      handleIfConditionChange(
                                        editRuleIndex,
                                        index,
                                        "field",
                                        option.value
                                      );
                                      handleOnBlurUpdateRule(editRuleIndex);
                                    }
                                  }}
                                />
                              </FormGroup>
                              <FormGroup as={Col} md="3">
                                <Select
                                  options={[
                                    { value: "containsAny", label: "Contains" },
                                    {
                                      value: "doesNotContainAny",
                                      label: "Not Contains",
                                    },
                                    ...(condition.field === "vendor" ||
                                    condition.field === "customer"
                                      ? [
                                          { value: "equal", label: "Equals" },
                                          {
                                            value: "notEqual",
                                            label: "Not Equals",
                                          },
                                        ]
                                      : []),
                                  ]}
                                  value={{
                                    value: condition.operator,
                                    label:
                                      condition.operator === "containsAny"
                                        ? "Contains"
                                        : condition.operator ===
                                          "doesNotContainAny"
                                        ? "Not Contains"
                                        : condition.operator === "equal"
                                        ? "Equals"
                                        : "Not Equals",
                                  }}
                                  onChange={(option) => {
                                    if (option) {
                                      handleIfConditionChange(
                                        editRuleIndex,
                                        index,
                                        "operator",
                                        option.value
                                      );
                                      handleOnBlurUpdateRule(editRuleIndex);
                                    }
                                  }}
                                />
                              </FormGroup>
                              <FormGroup as={Col} md="4">
                                <Form.Control
                                  name="keyword"
                                  type="text"
                                  value={
                                    typeof condition.value === "object" &&
                                    "keywords" in condition.value
                                      ? condition.value.keywords.join(", ")
                                      : condition.value
                                  }
                                  onChange={(e) => {
                                    handleIfConditionChange(
                                      editRuleIndex,
                                      index,
                                      "value",
                                      e.target.value
                                    );
                                    setErrorMap({
                                      ...errorMap,
                                      keyword: !!!e.target.value,
                                    });
                                  }}
                                  onBlur={() => {
                                    handleOnBlurUpdateRule(editRuleIndex);
                                  }}
                                />
                                {errorMap.keyword && (
                                  <ErrorMessage>
                                    {condition.field === "vendor" ||
                                    condition.field === "customer"
                                      ? `${
                                          condition.field
                                            .charAt(0)
                                            .toUpperCase() +
                                          condition.field.slice(1)
                                        } is required`
                                      : "Keyword is required"}
                                  </ErrorMessage>
                                )}
                              </FormGroup>
                              <FormGroup as={Col} md="1">
                                {index > 0 && (
                                  <Button
                                    variant="link"
                                    onClick={() =>
                                      handleRemoveIfCondition(
                                        editRuleIndex,
                                        index
                                      )
                                    }
                                  >
                                    <FontAwesomeIcon icon="trash" />
                                  </Button>
                                )}
                              </FormGroup>
                            </Form.Row>
                          </Form>
                        ))}
                        <Row>
                          <Col md="12">
                            <Button
                              variant="link"
                              onClick={() =>
                                handleAddIfCondition(editRuleIndex)
                              }
                              disabled={remainingFields.length === 0}
                            >
                              <FontAwesomeIcon icon="plus" />
                              <span className="ml-1">
                                Add another condition
                              </span>
                            </Button>
                          </Col>
                          <Col md="12">
                            <Form.Label>Then set</Form.Label>
                          </Col>
                        </Row>
                        {ruleToEdit.thenSet.map((thenSet, index) => (
                          <Form key={index}>
                            <Form.Row className="align-items-center">
                              <FormGroup as={Col} md="5">
                                <Select
                                  options={keys.map((option) => ({
                                    value: option,
                                    label: option,
                                  }))}
                                  value={
                                    thenSet.categoryAutomated
                                      ? {
                                          value: thenSet.categoryAutomated,
                                          label: thenSet.categoryAutomated,
                                        }
                                      : isXero
                                      ? {
                                          value: keys[0],
                                          label: keys[0],
                                        }
                                      : {
                                          value: categoryLabel,
                                          label: categoryLabel,
                                        }
                                  }
                                  onChange={(option) => {
                                    if (option) {
                                      handleRuleChange(
                                        editRuleIndex,
                                        index,
                                        "categoryAutomated",
                                        option.value
                                      );
                                      handleOnBlurUpdateRule(editRuleIndex);
                                    }
                                  }}
                                />
                              </FormGroup>
                              <FormGroup
                                as={Col}
                                md="1"
                                className="text-center"
                              >
                                <Form.Label>to</Form.Label>
                              </FormGroup>
                              <FormGroup as={Col} md="5">
                                {(thenSet.categoryAutomated === "Customer" ||
                                  thenSet.categoryAutomated === "Vendor") &&
                                integrationId ? (
                                  <AsyncSelect
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={loadContactOptions(
                                      thenSet.categoryAutomated as
                                        | "Customer"
                                        | "Vendor"
                                    )}
                                    value={
                                      thenSet.value
                                        ? {
                                            value: thenSet.value,
                                            label:
                                              thenSet.valueLabel ||
                                              thenSet.value,
                                          }
                                        : null
                                    }
                                    onChange={(option) => {
                                      if (option) {
                                        handleRuleChange(
                                          editRuleIndex,
                                          index,
                                          "value",
                                          option.value,
                                          option.label
                                        );
                                        handleOnBlurUpdateRule(editRuleIndex);
                                        setErrorMap({
                                          ...errorMap,
                                          category: !!!option,
                                        });
                                      }
                                    }}
                                    placeholder={`Search ${thenSet.categoryAutomated}s`}
                                    noOptionsMessage={({ inputValue }) =>
                                      !inputValue || inputValue.length < 2
                                        ? "Type to search"
                                        : "No matches"
                                    }
                                  />
                                ) : ["payables", "receivables"].includes(
                                  normalizedDocType
                                ) ? (
                                  <Form.Control
                                    type="text"
                                    value={thenSet.value}
                                    onChange={(e) => {
                                      handleRuleChange(
                                        editRuleIndex,
                                        index,
                                        "value",
                                        e.target.value
                                      );
                                      setErrorMap({
                                        ...errorMap,
                                        category: !!!e.target.value,
                                      });
                                    }}
                                    onBlur={() => {
                                      handleOnBlurUpdateRule(editRuleIndex);
                                    }}
                                  />
                                ) : integrationId ? (
                                  <AsyncSelect
                                    cacheOptions
                                    defaultOptions={
                                      isXero &&
                                      thenSet.categoryAutomated === "Account"
                                        ? groupAccountsByType(xeroAccounts)
                                        : isXero &&
                                          thenSet.categoryAutomated ===
                                            "Item/Service"
                                        ? xeroItems.map((item) => ({
                                            label: formatItemLabel(item),
                                            value: item.value,
                                          }))
                                        : categoryOptions
                                    }
                                    loadOptions={loadCategoryOrServiceOptions}
                                    value={{
                                      value: thenSet.value,
                                      label:
                                        isXero &&
                                        thenSet.categoryAutomated === "Account"
                                          ? (() => {
                                              const account = xeroAccounts.find(
                                                (a) => a.value === thenSet.value
                                              );
                                              return account
                                                ? formatAccountLabel(account)
                                                : thenSet.value;
                                            })()
                                          : isXero &&
                                            thenSet.categoryAutomated ===
                                              "Item/Service"
                                          ? (() => {
                                              const item = xeroItems.find(
                                                (i) => i.value === thenSet.value
                                              );
                                              return item
                                                ? formatItemLabel(item)
                                                : thenSet.value;
                                            })()
                                          : (categories || []).find(
                                              (c) => c.value === thenSet.value
                                            )?.name || thenSet.value,
                                    }}
                                    onChange={(option) => {
                                      if (option) {
                                        handleRuleChange(
                                          editRuleIndex,
                                          index,
                                          "value",
                                          option.value
                                        );
                                        handleOnBlurUpdateRule(editRuleIndex);
                                        setErrorMap({
                                          ...errorMap,
                                          category: !!!option,
                                        });
                                      }
                                    }}
                                    noOptionsMessage={({ inputValue }) =>
                                      !inputValue || inputValue.length < 2
                                        ? "Type at least 2 characters to search"
                                        : "No options found"
                                    }
                                    placeholder="Type to search"
                                  />
                                ) : (
                                  <Select
                                    options={categoryOptions}
                                    value={{
                                      value: thenSet.value,
                                      label:
                                        (categories || []).find(
                                          (c) => c.value === thenSet.value
                                        )?.name || thenSet.value,
                                    }}
                                    onChange={(option) => {
                                      if (option) {
                                        handleRuleChange(
                                          editRuleIndex,
                                          index,
                                          "value",
                                          option.value
                                        );
                                        handleOnBlurUpdateRule(editRuleIndex);
                                        setErrorMap({
                                          ...errorMap,
                                          category: !!!option,
                                        });
                                      }
                                    }}
                                  />
                                )}
                                {errorMap.category && (
                                  <ErrorMessage>
                                    {isXero
                                      ? thenSet.categoryAutomated ||
                                        "Account or Item/Service"
                                      : categoryLabel}{" "}
                                    is required
                                  </ErrorMessage>
                                )}
                              </FormGroup>
                              <FormGroup as={Col} md="1">
                                {index > 0 && (
                                  <Button
                                    variant="link"
                                    onClick={() =>
                                      handleRemoveThenSet(editRuleIndex, index)
                                    }
                                  >
                                    <FontAwesomeIcon icon="trash" />
                                  </Button>
                                )}
                              </FormGroup>
                            </Form.Row>
                          </Form>
                        ))}
                        <Button
                          variant="link"
                          onClick={() => handleAddThenSet(editRuleIndex)}
                          className="mb-2 ml-2"
                          disabled={remainingCategories.length === 0}
                        >
                          <FontAwesomeIcon icon="plus" />
                          <span className="ml-1">Add another action</span>
                        </Button>
                      </Card.Body>
                      {addingNewRule && (
                        <Card.Footer>
                          <LoadingButton
                            isLoading={runInvoiceRulesLoading}
                            variant="primary"
                            onClick={handleOnCreateRule}
                            className="float-right"
                            disabled={hasError}
                          >
                            <FontAwesomeIcon icon="plus" />
                            <span className="ml-1">Add new rule</span>
                          </LoadingButton>
                        </Card.Footer>
                      )}
                    </Card>
                  </Col>
                )}
              </Row>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={toggle}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
export default TagRegexesInvoice;
